import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, withRouter } from "react-router-dom";
import { debounce } from "lodash";
import { classnames, Hamburger, Icon, Nav } from "@culturehq/components";

import { useProfile } from "app/context";
import { useNotificationReceivedListener } from "app/hooks";
import { isAdmin, isRecruiter } from "app/permissions";
import useUnseen from "app/context/useUnseen";
import { t } from "app/utils";

import notificationsTypes from "constants/notificationsTypes";

import FeedNavItem from "./navigation/FeedNavItem";
import Notifications from "./navigation/Notifications";
import Profile from "./navigation/Profile";
import SearchBar from "./navigation/SearchBar";
import SearchResults from "./navigation/SearchResults";
import { useFeedRoute } from "./Home";

const NEXT_PAGE_DELAY = 300;

const NavItem = ({ exact, to, icon, label, isActive, onClick }) => (
  <li>
    <NavLink exact={exact} to={to} isActive={isActive} onClick={onClick}>
      <Icon icon={icon} /> {label}
    </NavLink>
  </li>
);

const useAutoClose = history => {
  const [open, setOpen] = useState(false);
  const onToggle = useCallback(() => setOpen(value => !value), [setOpen]);

  useEffect(() => history.listen(() => setOpen(false)), [history, setOpen]);

  return [open, onToggle];
};

const Navigation = withRouter(({ history }) => {
  const { profile, refreshProfile } = useProfile();
  const [feedRoute] = useFeedRoute(profile);
  const unseen = useUnseen(feedRoute);

  const [mobileOpen, onMobileToggle] = useAutoClose(history);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [{ page, pagination }, setPage] = useState({ page: 1, pagination: {} });

  useNotificationReceivedListener(
    useCallback(
      async notification => {
        switch (notification.notificationType) {
          case notificationsTypes.CHANGE_TENANT:
            refreshProfile();
            window.location.reload();
            break;
          default:
            break;
        }
      },
      [refreshProfile]
    )
  );

  const resetPagination = () => setPage({ page: 1, pagination: {} });

  const handleClearSearchResults = useCallback(
    () => {
      resetPagination();
      setSearchQuery("");
      setSearchResults(null);
    },
    [setSearchQuery, setSearchResults]
  );

  const handleSearchQueryChange = useCallback(
    query => {
      if (!query) handleClearSearchResults();
      else {
        resetPagination();
        setSearchQuery(query);
      }
    },
    [handleClearSearchResults, setSearchQuery]
  );

  const handleSearchResults = useCallback(
    (results, pagination) => {
      setSearchResults(prev => {
        if (!results?.length || pagination.currentPage === 1) return results;
        return [...prev, ...results];
      });
      setPage(prev => ({ ...prev, pagination }));
    },
    [setSearchResults]
  );

  const handleNextPage = () => {
    if (!pagination.totalPages || pagination.currentPage >= pagination.totalPages) return;

    setPage(prev => ({ ...prev, page: pagination.currentPage + 1 }));
  };
  const debouncedHandleNextPage = useCallback(debounce(handleNextPage, NEXT_PAGE_DELAY),
    [pagination]
  );

  return (
    <Nav className={classnames({ "chq-nav-mopen": mobileOpen }, `${profile?.organization?.name?.replace(/\s+/g, "-")?.toLowerCase()}-nav`)}>
      <section role="navigation" aria-label="Notifications and settings">
        <div className="pull-right right-menu">
          <div className="chq-nav--admin__container">
            <div className="chq-nav--admin">
              {isAdmin(profile) ? (
                <Link to={unseen.pendingStoryReviews > 0 ? "/admin/story-reviews/pending" : "/admin/story-questions"} className="chq-nav--admint">
                  <Icon icon="gear-b" /><span>Admin</span>
                  {unseen.pendingStoryReviews > 0 && <em className="Notification-Badge">{unseen.pendingStoryReviews}</em>}
                </Link>
              ) : isRecruiter(profile) && (
                <Link to="/admin/story-reviews/approved" className="chq-nav--admint">
                  <Icon icon="gear-b" /><span>Recruiter</span>
                </Link>
              )}
            </div>
          </div>
          <Notifications />
          <Profile />
        </div>
        <Hamburger open={mobileOpen} onToggle={onMobileToggle} />
      </section>
      <SearchBar
        searchQuery={searchQuery}
        page={page}
        onSearchQueryChange={handleSearchQueryChange}
        onSearchResults={handleSearchResults}
      />
      <div className="chq-nav--sep" />
      <section className="chq-nav--sec" role="navigation" aria-label="Main">
        <ul className="chq-nav--items">
          <FeedNavItem />
          <NavItem to="/community" icon="ios-people" label={t("Community")} />
          {!isRecruiter(profile) && (
            <>
              {profile.organization.eventsEnabled && (
                <NavItem to="/events" icon="calendar-filled" label={t("Events")} />
              )}
              {profile.organization.recognitionsEnabled && (
                <NavItem to="/recognitions" icon="megaphone" label={t("Recognitions")} />
              )}
              {profile.organization.surveysEnabled && (
                <NavItem to="/surveys" icon="checklist" label={t("Surveys")} />
              )}
            </>
          )}
        </ul>
      </section>
      <SearchResults
        searchResults={searchResults}
        onClearSearchResults={handleClearSearchResults}
        onNextPage={debouncedHandleNextPage}
      />
    </Nav>
  );
});

export default Navigation;
